/**
 * Google Tag Manager Tracking Utility
 *
 * Pushes events to GTM dataLayer for GA4 and Google Ads conversion tracking.
 */

import { shouldFirePixelEvents } from "./pixelUtils";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * Fire a GA4 event via gtag(), with iframe parent-window support.
 */
const gtagEvent = (eventName: string, params?: Record<string, any>): void => {
  if (!shouldFirePixelEvents()) {
    return;
  }

  const getDataLayer = (): any[] | null => {
    // Check parent window first (for iframe), then current window
    try {
      if (window.parent?.dataLayer) {
        return window.parent.dataLayer;
      }
    } catch {
      console.log("[GTM] Cross-origin access blocked, trying current window");
    }

    return window.dataLayer ?? null;
  };

  const dataLayer = getDataLayer();

  if (dataLayer) {
    dataLayer.push({ event: eventName, ...params });
  } else if (import.meta.env.MODE === "development") {
    console.log(
      `[GTM - DEV] dataLayer not available. Would fire: ${eventName}`,
      params,
    );
  }
};

/**
 * Track GA4 generate_lead event (maps to Facebook Lead event in SignUp.tsx)
 */
export const trackGA4Lead = (params?: Record<string, any>): void => {
  gtagEvent("generate_lead", {
    content_name: "Landing Page Visit",
    ...params,
  });
};

/**
 * Track GA4 sign_up event (maps to Facebook CompleteRegistration in AppointmentCreatedScreen)
 */
export const trackGA4CompleteRegistration = (
  params?: Record<string, any>,
): void => {
  gtagEvent("sign_up", {
    value: 0.0,
    currency: "USD",
    content_name: "Appointment Booking Complete",
    ...params,
  });
};
